import React, { useMemo } from 'react';
import { Button, ButtonGroup, Checkbox, ModalBox, Table, Toasts, Tooltip, useMediaQueryContext, useModalContext, } from '@fourthwall/components';
import * as S from './GiveawayTwitchRedo.styled';
import { useTableItems } from './hooks/useTableItems';
import { useGiveawayConfigStreamQuery } from './queries/useGiveawayConfigStreamQuery';
import { useRedoGiveawayMutation } from './queries/useRedoGiveawayMutation';
export const GiveawayTwitchRedo = ({ giveawayId }) => {
    const media = useMediaQueryContext();
    const { giveawayConfigStreamQuery } = useGiveawayConfigStreamQuery();
    const { redoGiveawayMutation } = useRedoGiveawayMutation();
    const { close } = useModalContext();
    const { tableItems, selectedTableItemsIds, stats } = useTableItems(giveawayId);
    const isStreamLive = giveawayConfigStreamQuery.isSuccess && giveawayConfigStreamQuery.data.type === 'STREAM_LIVE';
    const hasTableItems = !!tableItems.length;
    const noTableItemSelected = hasTableItems && !selectedTableItemsIds.length;
    const submitButtonTooltip = useMemo(() => {
        if (stats?.gifts.available === 0 && stats?.gifts.noWinner === 0) {
            return 'You cannot redo giveaways that have been already redeemed';
        }
        if (!isStreamLive) {
            return 'You must be live to redo a giveaway';
        }
        if (noTableItemSelected) {
            return 'You must select at least one gift';
        }
        return undefined;
    }, [stats?.gifts.available, stats?.gifts.noWinner, isStreamLive, noTableItemSelected]);
    const getSubmitButtonLabel = () => {
        if (stats?.gifts.noWinner && stats?.gifts.noWinner > 0) {
            return `Redo ${stats?.gifts.noWinner} ${stats?.gifts.noWinner > 1 ? 'giveaways' : 'giveaway'}`;
        }
        return 'Redo giveaway';
    };
    const handleSubmitSuccess = (data) => {
        close();
        Toasts.notify(`Giveaway will start shortly at ${data.twitch.twitchChannel}. Wait for the results of a new draw.`, { type: 'info' });
    };
    const handleSubmitButtonClick = () => {
        if (!hasTableItems) {
            redoGiveawayMutation.mutate({
                params: { giveawayId },
                payload: { gifts: undefined },
            }, {
                onSuccess: handleSubmitSuccess,
            });
        }
        else {
            redoGiveawayMutation.mutate({
                params: { giveawayId },
                payload: { gifts: selectedTableItemsIds },
            }, {
                onSuccess: handleSubmitSuccess,
            });
        }
    };
    const renderSubtitle = () => {
        if (!tableItems?.length) {
            return (React.createElement(React.Fragment, null, "This gift currently has no winner. You can redo the giveaway to immediately begin a new entry window on your stream."));
        }
        return (React.createElement(React.Fragment, null, "Select the giveaways you want to redo. Existing winners will no longer be able to redeem. The new giveaways will begin immediately on your stream."));
    };
    const renderTable = () => {
        if (!tableItems?.length) {
            return null;
        }
        return (React.createElement(S.Winners, null,
            React.createElement(Table, { data: tableItems, minWidth: 0, headers: [
                    {
                        id: 'winner',
                        label: 'Twitch gifting winner',
                        height: 48,
                        width: 1,
                        render: ({ name, isSelected, isDisabled, onClick }) => (React.createElement(Checkbox, { value: isSelected, disabled: isDisabled, label: name, onValueChange: onClick })),
                    },
                    {
                        id: 'redeemed',
                        label: 'Redemeed',
                        height: 48,
                        width: 0,
                        minWidth: 120,
                        alignment: 'right',
                        render: ({ redeemDate }) => redeemDate || 'Not redeemed',
                    },
                ] })));
    };
    return (React.createElement(ModalBox, { title: "Redo giveaway on Twitch", onClose: close, size: "small" },
        renderSubtitle(),
        renderTable(),
        React.createElement(S.Buttons, null,
            React.createElement(ButtonGroup, { justify: "right", align: "center", space: media.tablet ? 'large' : 'medium', direction: media.tablet ? 'row' : 'column' },
                React.createElement(Button, { label: "Cancel", appearance: "secondary", onClick: close }),
                React.createElement(Tooltip, { reference: React.createElement(Button, { label: getSubmitButtonLabel(), appearance: "primary", disabled: !isStreamLive || noTableItemSelected, loading: redoGiveawayMutation.isLoading, onClick: handleSubmitButtonClick }), placement: "top", fluid: true }, submitButtonTooltip && React.createElement(S.TooltipContent, null, submitButtonTooltip))))));
};
